// Analytics Service for Grey Coffee
// Handles data collection, user behavior tracking, and consent management

import visitorTracking from './visitorTracking';

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.consent = this.getStoredConsent();
    this.sessionData = {
      startTime: Date.now(),
      pageViews: [],
      interactions: [],
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language
    };
    
    this.init();
  }

  init() {
    if (this.isInitialized) return;
    
    // Track initial page load
    this.trackPageView(window.location.pathname);
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Start session tracking
    this.startSessionTracking();
    
    this.isInitialized = true;
  }

  getStoredConsent() {
    try {
      const stored = localStorage.getItem('grey-cookie-consent');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error reading consent preferences:', error);
      return null;
    }
  }

  hasConsent(type) {
    return this.consent && this.consent[type] === true;
  }

  updateConsent(newConsent) {
    this.consent = newConsent;
    
    // Initialize or disable tracking based on consent
    if (newConsent.analytics) {
      this.initializeAnalytics();
    } else {
      this.disableAnalytics();
    }
    
    if (newConsent.marketing) {
      this.initializeMarketing();
    } else {
      this.disableMarketing();
    }
  }

  // Page tracking
  trackPageView(path, title = document.title) {
    const pageView = {
      path,
      title,
      timestamp: Date.now(),
      referrer: document.referrer,
      userAgent: navigator.userAgent
    };
    
    this.sessionData.pageViews.push(pageView);
    
    // Send to analytics if consent given
    if (this.hasConsent('analytics')) {
      this.sendToAnalytics('page_view', pageView);
      
      // Also track with Firebase visitor tracking
      visitorTracking.trackPageView(path);
    }
    
    // Store locally for business insights
    this.storeDataLocally('pageViews', pageView);
  }

  // Event tracking for business insights
  trackEvent(category, action, label = null, value = null) {
    const event = {
      category,
      action,
      label,
      value,
      timestamp: Date.now(),
      path: window.location.pathname
    };
    
    this.sessionData.interactions.push(event);
    
    // Send to analytics if consent given
    if (this.hasConsent('analytics')) {
      this.sendToAnalytics('event', event);
      
      // Also track with Firebase visitor tracking
      visitorTracking.trackEvent(category, action, label, value);
    }
    
    // Always store business-critical events locally
    this.storeDataLocally('events', event);
  }

  // E-commerce tracking
  trackPurchase(orderData) {
    const purchase = {
      orderId: orderData.orderId,
      total: orderData.total,
      items: orderData.items,
      timestamp: Date.now(),
      currency: 'QAR'
    };
    
    if (this.hasConsent('analytics')) {
      this.sendToAnalytics('purchase', purchase);
    }
    
    // Always store purchase data (essential for business)
    this.storeDataLocally('purchases', purchase);
  }

  // User behavior insights
  trackMenuInteraction(action, itemData) {
    this.trackEvent('Menu', action, itemData.name, itemData.price);
  }

  trackCartAction(action, itemData) {
    this.trackEvent('Cart', action, itemData.name, itemData.quantity);
  }

  trackLocationClick(locationName) {
    this.trackEvent('Location', 'view_location', locationName);
  }

  trackSearch(searchTerm, resultsCount) {
    this.trackEvent('Search', 'search', searchTerm, resultsCount);
  }

  trackBooking(action, bookingData) {
    this.trackEvent('Booking', action, bookingData.location, bookingData.guests);
  }

  // Setup event listeners for automatic tracking
  setupEventListeners() {
    // Track outbound links
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (link && link.href && !link.href.includes(window.location.hostname)) {
        this.trackEvent('Outbound', 'click', link.href);
      }
    });

    // Track form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.id || form.className) {
        this.trackEvent('Form', 'submit', form.id || form.className);
      }
    });

    // Track scroll depth
    let maxScroll = 0;
    window.addEventListener('scroll', this.throttle(() => {
      const scrollPercent = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
        if ([25, 50, 75, 90].includes(scrollPercent)) {
          this.trackEvent('Scroll', 'depth', window.location.pathname, scrollPercent);
        }
      }
    }, 1000));

    // Track time on page
    window.addEventListener('beforeunload', () => {
      const timeOnPage = Date.now() - this.sessionData.startTime;
      this.trackEvent('Engagement', 'time_on_page', window.location.pathname, timeOnPage);
    });
  }

  // Session tracking
  startSessionTracking() {
    // Update session info every 30 seconds
    setInterval(() => {
      this.updateSessionData();
    }, 30000);
  }

  updateSessionData() {
    this.sessionData.sessionDuration = Date.now() - this.sessionData.startTime;
    this.sessionData.lastActivity = Date.now();
    
    // Store session data
    if (this.hasConsent('functional')) {
      localStorage.setItem('grey-session-data', JSON.stringify(this.sessionData));
    }
  }

  // Data storage
  storeDataLocally(type, data) {
    try {
      const key = `grey-analytics-${type}`;
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      existing.push(data);
      
      // Keep only last 100 entries to prevent storage bloat
      if (existing.length > 100) {
        existing.splice(0, existing.length - 100);
      }
      
      localStorage.setItem(key, JSON.stringify(existing));
    } catch (error) {
      console.error('Error storing analytics data:', error);
    }
  }

  // Send to external analytics (Google Analytics, etc.)
  sendToAnalytics(type, data) {
    if (typeof window.gtag !== 'undefined') {
      switch (type) {
        case 'page_view':
          window.gtag('event', 'page_view', {
            page_title: data.title,
            page_location: window.location.href
          });
          break;
        case 'event':
          window.gtag('event', data.action, {
            event_category: data.category,
            event_label: data.label,
            value: data.value
          });
          break;
        case 'purchase':
          window.gtag('event', 'purchase', {
            transaction_id: data.orderId,
            value: data.total,
            currency: data.currency,
            items: data.items.map(item => ({
              item_id: item.id,
              item_name: item.name,
              price: item.price,
              quantity: item.quantity
            }))
          });
          break;
        default:
          break;
      }
    }
  }

  // Initialize external services
  initializeAnalytics() {
    // Google Analytics 4
    if (!window.gtag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID';
      document.head.appendChild(script);
      
      window.dataLayer = window.dataLayer || [];
      function gtag(){window.dataLayer.push(arguments);}
      window.gtag = gtag;
      window.gtag('js', new Date());
      window.gtag('config', 'GA_MEASUREMENT_ID'); // Replace with your GA4 ID
    }
  }

  initializeMarketing() {
    // Facebook Pixel
    if (!window.fbq) {
      (function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)})(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      
      if (window.fbq) {
        window.fbq('init', 'YOUR_PIXEL_ID'); // Replace with your pixel ID
        window.fbq('track', 'PageView');
      }
    }
  }

  disableAnalytics() {
    // Disable Google Analytics
    if (window.gtag) {
      window.gtag('config', 'GA_MEASUREMENT_ID', {
        send_page_view: false
      });
    }
  }

  disableMarketing() {
    // Clear marketing cookies
    this.clearMarketingCookies();
  }

  clearMarketingCookies() {
    // Clear Facebook and other marketing cookies
    const cookiesToClear = ['_fbp', '_fbc', '_ga', '_gid', '_gat'];
    cookiesToClear.forEach(cookie => {
      document.cookie = `${cookie}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  }

  // Get analytics data for admin dashboard
  getAnalyticsData() {
    const data = {};
    const keys = ['pageViews', 'events', 'purchases'];
    
    keys.forEach(key => {
      try {
        data[key] = JSON.parse(localStorage.getItem(`grey-analytics-${key}`) || '[]');
      } catch (error) {
        data[key] = [];
      }
    });
    
    return data;
  }

  // Utility functions
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => { inThrottle = false; }, limit);
      }
    };
  }

  // GDPR Compliance - allow users to export their data
  exportUserData() {
    const userData = {
      consent: this.consent,
      sessionData: this.sessionData,
      analyticsData: this.getAnalyticsData(),
      exportDate: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(userData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `grey-coffee-data-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    
    URL.revokeObjectURL(url);
  }

  // Delete all user data
  deleteUserData() {
    const keys = [
      'grey-cookie-consent',
      'grey-cookie-consent-date',
      'grey-session-data',
      'grey-analytics-pageViews',
      'grey-analytics-events',
      'grey-analytics-purchases'
    ];
    
    keys.forEach(key => localStorage.removeItem(key));
    this.clearMarketingCookies();
    
    // Reset instance
    this.consent = null;
    this.sessionData = {
      startTime: Date.now(),
      pageViews: [],
      interactions: []
    };
  }
}

// Create singleton instance
const analytics = new AnalyticsService();

export default analytics; 